export const PROJECTS_DATA = [
  {
    _id: '1',
    slug: 'neuroops-ai-manufacturing',
    title: 'NeuroOps — AI Manufacturing Intelligence',
    tagline: 'Industry 5.0 Human-Machine Collaboration at Scale',
    category: 'Industry 5.0',
    featured: true,
    image: 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?auto=format&fit=crop&w=800&q=80',
    heroImage: 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?auto=format&fit=crop&w=1400&q=85',
    description:
      'An end-to-end AI manufacturing intelligence layer combining digital twins, cobot orchestration, and real-time predictive maintenance — cutting unplanned downtime by 67%.',
    techStack: ['Python', 'PyTorch', 'Digital Twin', 'ROS2', 'Apache Kafka', 'Azure IoT Hub'],
    metrics: [
      { label: 'Downtime Reduction', value: '67%' },
      { label: 'OEE Improvement', value: '+31 pts' },
      { label: 'Monthly Cost Savings', value: '$420K' },
      { label: 'Sensors Integrated', value: '12,000+' },
    ],
    overview:
      "A global Tier-1 automotive components manufacturer was struggling with unpredictable equipment failures across 4 production plants. Manual inspection cycles couldn't keep pace with 24/7 operations. They needed an intelligent, autonomous layer that could see across every machine, cobot, and conveyor — and act before failure occurred.",
    problem:
      "The client's existing SCADA system generated 18 TB of sensor data per month but had no intelligence layer. Engineers spent 60% of their time on reactive maintenance, and three major unplanned shutdowns in one quarter had cost $1.2M in lost production. The siloed data environment meant cross-plant learning was impossible.",
    solution:
      'Zynexis built NeuroOps — a multi-layer AI platform that ingests raw telemetry from 12,000+ sensors, trains plant-specific anomaly models using PyTorch, and orchestrates cobot workflows via ROS2. A live digital twin mirrors every physical asset, letting operations teams simulate interventions before executing them on the floor. Kafka pipelines deliver sub-100ms event streams to the real-time alerting dashboard.',
    results:
      'Within 6 months of go-live, unplanned downtime fell 67%. OEE climbed 31 percentage points. The digital twin enabled 3 full line-rebalancing simulations that would previously have required physical shutdowns. The client achieved full ROI in under 8 months and is now rolling out to 6 additional plants.',
    timeline: '9 months',
    teamSize: '12 engineers',
    industry: 'Manufacturing / Automotive',
    deliverables: [
      'AI Anomaly Detection Engine',
      'Real-time Digital Twin',
      'Cobot Orchestration Layer',
      'Operations Control Dashboard',
      'Mobile Maintenance App',
    ],
    gallery: [
      'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=800&q=80',
      'https://images.unsplash.com/photo-1565043666747-69f6646db940?auto=format&fit=crop&w=800&q=80',
    ],
  },
  {
    _id: '2',
    slug: 'greengrid-energy-os',
    title: 'GreenGrid — Distributed Energy OS',
    tagline: 'Edge AI–Powered Renewable Microgrid Optimisation',
    category: 'CleanTech',
    featured: true,
    image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?auto=format&fit=crop&w=800&q=80',
    heroImage: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?auto=format&fit=crop&w=1400&q=85',
    description:
      'A cloud-native energy management OS for distributed solar-wind microgrids, using edge AI to balance loads in real time and reduce grid import costs by 44%.',
    techStack: ['TensorFlow Lite', 'Rust', 'MQTT', 'InfluxDB', 'Kubernetes', 'React'],
    metrics: [
      { label: 'Grid Import Reduction', value: '44%' },
      { label: 'Renewable Utilisation', value: '91%' },
      { label: 'CO₂ Offset / Year', value: '8,200 t' },
      { label: 'Sites Managed', value: '340+' },
    ],
    overview:
      'A renewable energy cooperative managing 340 solar-wind microgrid sites across Southeast Asia needed a unified intelligence platform. Each site had unique load profiles, battery chemistries, and grid tariff structures — traditional SCADA solutions could not handle the heterogeneity at scale.',
    problem:
      'Without dynamic load balancing, peak-time grid imports were spiking energy costs 38% above forecast. Storage assets were being cycled aggressively, degrading battery life 2× faster than expected. The operator was managing each site in isolation with spreadsheets and seven disparate vendor tools.',
    solution:
      'Zynexis designed GreenGrid OS in three tiers: on-site edge devices running TensorFlow Lite forecasting models with zero cloud dependency, a regional aggregation layer built in Rust for microsecond-latency routing decisions, and a cloud fleet dashboard with InfluxDB time-series analytics. The system ingests weather forecasts, real-time pricing signals, and demand patterns to compute optimal battery dispatch strategies every 15 seconds.',
    results:
      'Grid import costs dropped 44% in the first quarter post-deployment. Renewable self-consumption reached 91%, up from 63%. Battery cycle degradation slowed by 55%, extending projected asset lifetimes by 4 years. Carbon offset reporting — previously a manual quarterly exercise — now runs in real time.',
    timeline: '11 months',
    teamSize: '9 engineers',
    industry: 'CleanTech / Energy',
    deliverables: [
      'Edge AI Forecasting Engine',
      'Real-time Dispatch Optimizer',
      'Fleet Management Dashboard',
      'Battery Health Monitor',
      'Carbon Reporting Module',
    ],
    gallery: [
      'https://images.unsplash.com/photo-1466611653911-95081537e5b7?auto=format&fit=crop&w=800&q=80',
      'https://images.unsplash.com/photo-1548337138-e87d889cc369?auto=format&fit=crop&w=800&q=80',
    ],
  },
  {
    _id: '3',
    slug: 'medisync-health-network',
    title: 'MediSync — Autonomous Health Network',
    tagline: 'AI Diagnostics + IoT Wearables + Predictive Care',
    category: 'HealthTech',
    featured: true,
    image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=800&q=80',
    heroImage: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=1400&q=85',
    description:
      'A next-generation telehealth platform bridging AI diagnostic models with IoT wearable data streams, enabling early disease detection and reducing hospital readmissions by 41%.',
    techStack: ['React Native', 'FastAPI', 'TensorFlow', 'WebRTC', 'FHIR R4', 'AWS HealthLake'],
    metrics: [
      { label: 'Readmission Reduction', value: '41%' },
      { label: 'Active Patients', value: '180K+' },
      { label: 'Avg Triage Time', value: '8 min' },
      { label: 'AI Triage Accuracy', value: '94.2%' },
    ],
    overview:
      'A national hospital network spanning 28 facilities needed to bridge the gap between in-person care and the 73% of patients discharging without adequate remote follow-up. Readmission rates were 18% above the national benchmark, driving significant penalty costs and preventable adverse events.',
    problem:
      'Clinicians had no unified view of a patient\'s post-discharge health trajectory. Wearable data lived in third-party apps with no clinical integration. The triage process averaged 47 minutes per patient, creating ER backlogs during peak hours. HIPAA and HL7 data governance requirements were blockers for any vendor integration.',
    solution:
      'Zynexis built MediSync on a FHIR R4–compliant data backbone, integrating 14 wearable device SDKs into a single longitudinal patient timeline. A TensorFlow-powered triage engine analyses vitals, medical history, and symptom flags in real time, surfacing risk scores to clinicians before they enter the consultation room. WebRTC enables sub-500ms HD video consultations with adaptive quality for low-bandwidth connections.',
    results:
      'Hospital readmissions fell 41% within the first 6 months. Average triage time dropped from 47 minutes to 8 minutes. The AI diagnostic model reached 94.2% accuracy on the independent validation dataset. The platform scaled to 180,000 active patients with zero data breaches and full HIPAA attestation.',
    timeline: '14 months',
    teamSize: '16 engineers',
    industry: 'Healthcare / HealthTech',
    deliverables: [
      'AI Triage & Risk Engine',
      'FHIR Patient Data Fabric',
      'IoT Wearable Integration Hub',
      'HD Video Consultation Platform',
      'Clinical Analytics Dashboard',
    ],
    gallery: [
      'https://images.unsplash.com/photo-1584820927498-cfe5211fd8bf?auto=format&fit=crop&w=800&q=80',
      'https://images.unsplash.com/photo-1530497610245-94d3c16cda28?auto=format&fit=crop&w=800&q=80',
    ],
  },
  {
    _id: '4',
    slug: 'quantumpay-defi-gateway',
    title: 'QuantumPay — Web3 Payment Rail',
    tagline: 'Multi-Chain DeFi Settlement with AI Fraud Intelligence',
    category: 'FinTech',
    featured: false,
    image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?auto=format&fit=crop&w=800&q=80',
    heroImage: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?auto=format&fit=crop&w=1400&q=85',
    description:
      'A PCI-DSS + SOC 2 compliant, multi-chain payment gateway combining DeFi liquidity rails with AI fraud detection, processing $4M+ in daily volume with sub-2s settlement.',
    techStack: ['Solidity', 'Node.js', 'Rust', 'PostgreSQL', 'Redis', 'Ethereum / Polygon'],
    metrics: [
      { label: 'Daily Volume', value: '$4M+' },
      { label: 'Settlement Time', value: '<2 sec' },
      { label: 'Fraud Detection Rate', value: '99.1%' },
      { label: 'Platform Uptime', value: '99.99%' },
    ],
    overview:
      'A cross-border payments fintech wanted to build a next-generation settlement rail combining the programmability of DeFi with the compliance demands of institutional clients. They needed sub-2-second settlement across 14 currencies and a fraud detection layer capable of operating at 10,000 TPS.',
    problem:
      "Their legacy Swift-based rails took 2–5 business days for cross-border settlement, locking up $28M in working capital as float at any given time. Fraud chargebacks were running at 1.4% of volume. PCI-DSS Level 1 and multi-jurisdictional AML compliance requirements were blocking expansion into three new markets.",
    solution:
      'Zynexis architected QuantumPay as a hybrid custody model — Ethereum and Polygon smart contracts handle on-chain settlement for crypto corridors; a traditional custodial rail serves regulated fiat routes. A Rust-based transaction engine processes signature verification and routing at microsecond latency. The ML fraud module trains on graph-based transaction networks, detecting synthetic identity rings and velocity attacks invisible to rules-based engines.',
    results:
      'Settlement latency collapsed from days to under 2 seconds for supported corridors. Float reduction freed $22M in working capital immediately at launch. Fraud dropped from 1.4% to 0.09% — a 93% improvement. The platform achieved PCI-DSS Level 1 certification and SOC 2 Type II attestation on first audit.',
    timeline: '12 months',
    teamSize: '14 engineers',
    industry: 'FinTech / Web3',
    deliverables: [
      'Multi-Chain Smart Contract Suite',
      'High-Throughput Transaction Engine',
      'AI Graph Fraud Detection',
      'Compliance & AML Dashboard',
      'Merchant SDK & REST APIs',
    ],
    gallery: [
      'https://images.unsplash.com/photo-1621761191319-c6fb62004040?auto=format&fit=crop&w=800&q=80',
      'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&w=800&q=80',
    ],
  },
  {
    _id: '5',
    slug: 'eduverse-immersive-learning',
    title: 'EduVerse — Immersive Learning Metaverse',
    tagline: 'AI-Tutored XR Classrooms for the Next Billion Learners',
    category: 'EdTech',
    featured: false,
    image: 'https://images.unsplash.com/photo-1593508512255-86ab42a8e620?auto=format&fit=crop&w=800&q=80',
    heroImage: 'https://images.unsplash.com/photo-1593508512255-86ab42a8e620?auto=format&fit=crop&w=1400&q=85',
    description:
      'A WebXR-based adaptive learning metaverse featuring AI tutors, virtual science labs, and spaced-repetition algorithms — increasing student knowledge retention by 58%.',
    techStack: ['WebXR', 'React', 'Three.js', 'OpenAI API', 'GraphQL', 'Supabase'],
    metrics: [
      { label: 'Knowledge Retention Lift', value: '+58%' },
      { label: 'Active Learners', value: '320K+' },
      { label: 'Course Completion Rate', value: '76%' },
      { label: 'Avg Session Time', value: '47 min' },
    ],
    overview:
      'A pan-African EdTech startup wanted to democratise access to world-class STEM education using immersive technology. Their target audience — students in bandwidth-constrained environments — needed an experience as engaging as gaming yet pedagogically rigorous, accessible from a mid-range Android device.',
    problem:
      'Traditional e-learning platforms saw 78% drop-off before course completion. Passive video content produced negligible knowledge retention. The client had no adaptive content delivery, and their existing platform loaded poorly on mid-range Android devices common in their key markets.',
    solution:
      'Zynexis built EduVerse on a progressive WebXR stack that degrades seamlessly from VR headsets down to standard mobile browsers without content loss. An OpenAI-powered AI tutor dynamically adjusts explanations and difficulty based on each student\'s assessed comprehension. Three.js-rendered virtual chemistry, physics, and biology labs let students run experiments impossible in under-resourced schools. A spaced-repetition scheduler personalises review sessions using real-time performance analytics.',
    results:
      'Course completion rates jumped from 22% to 76%. Knowledge retention studies showed a 58% lift versus control groups on equivalent content. Average session time reached 47 minutes — 4× the industry average. EduVerse now serves 320,000+ active learners across 14 countries.',
    timeline: '10 months',
    teamSize: '11 engineers',
    industry: 'EdTech / Extended Reality',
    deliverables: [
      'WebXR Virtual Lab Engine',
      'AI Adaptive Tutor (GPT-4o)',
      'Spaced-Repetition Learning Engine',
      'Cross-Device Content Pipeline',
      'Educator Analytics Platform',
    ],
    gallery: [
      'https://images.unsplash.com/photo-1626379801357-537572d9f47a?auto=format&fit=crop&w=800&q=80',
      'https://images.unsplash.com/photo-1550745165-9bc0b252726f?auto=format&fit=crop&w=800&q=80',
    ],
  },
  {
    _id: '6',
    slug: 'chaintrace-supply-intelligence',
    title: 'ChainTrace — Supply Chain Intelligence',
    tagline: 'Blockchain-Verified Visibility with Real-Time ESG Scoring',
    category: 'Logistics',
    featured: false,
    image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=800&q=80',
    heroImage: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=1400&q=85',
    description:
      'An end-to-end supply chain intelligence platform using blockchain provenance records, IoT telemetry, and AI demand forecasting to slash inventory carrying costs by 35% and delivery delays by 52%.',
    techStack: ['Hyperledger Fabric', 'Vue.js', 'Apache Kafka', 'Elasticsearch', 'Docker', 'Python'],
    metrics: [
      { label: 'Inventory Cost Reduction', value: '35%' },
      { label: 'Forecast Accuracy', value: '96.4%' },
      { label: 'Supplier Nodes', value: '2,800+' },
      { label: 'Delivery Delays Cut', value: '52%' },
    ],
    overview:
      'A Fortune 500 FMCG manufacturer faced persistent supply disruptions and was carrying $180M in safety stock to buffer against unreliable demand signals. With 2,800 supplier nodes across 38 countries, their legacy ERP had zero real-time visibility beyond the immediate Tier-1 supplier layer.',
    problem:
      "The absence of multi-tier visibility meant the company was blindsided by sub-supplier disruptions, discovering stock-outs only after production lines stopped. Demand forecasting relied on 6-week-old ERP snapshots. ESG reporting on supply chain emissions required 11 weeks of manual reconciliation each quarter — an unsustainable burden ahead of new regulatory mandates.",
    solution:
      'Zynexis deployed ChainTrace on Hyperledger Fabric, giving every supply chain event — production lot, QA clearance, customs entry, last-mile handover — an immutable, multi-party verified blockchain record. A Kafka pipeline streams live IoT telemetry from cold-chain and transit assets. A Python ML forecasting engine ingests POS data, weather signals, and macro indicators to generate 96.4%-accurate 12-week demand plans. An automated ESG module computes carbon, water, and labour metrics per SKU from the blockchain ledger in real time.',
    results:
      'Inventory carrying costs fell 35% in year one, freeing $63M in working capital. Delivery delays dropped 52%. ESG reporting shrank from 11 weeks to 4 hours. The blockchain audit trail successfully resolved two major supplier dispute incidents, avoiding an estimated $8M in litigation costs.',
    timeline: '13 months',
    teamSize: '13 engineers',
    industry: 'Logistics / FMCG',
    deliverables: [
      'Blockchain Provenance Network',
      'Multi-Tier Visibility Dashboard',
      'AI Demand Forecasting Engine',
      'Cold-Chain IoT Monitor',
      'Automated ESG Reporting',
    ],
    gallery: [
      'https://images.unsplash.com/photo-1553413077-190dd305871c?auto=format&fit=crop&w=800&q=80',
      'https://images.unsplash.com/photo-1566576912321-d58ddd7a6088?auto=format&fit=crop&w=800&q=80',
    ],
  },
];
